export type UUID = string;
export type MoneyString = string;
export type ISODate = `${number}-${number}-${number}`;
export type EntryType = 'expense' | 'refund' | 'transfer';
export type SyncState = 'pending' | 'sending' | 'acknowledged' | 'conflict' | 'rejected';

export interface ScanDocumentInput {
  id: UUID;
  kind: 'receipt' | 'payment_slip';
  contentFingerprint: string;
  sourceIdentityHash?: string | null;
  parserVersion: string;
}

export interface Profile {
  id: UUID;
  displayName: string;
  avatarUrl?: string | null;
}

export interface Household {
  id: UUID;
  name: string;
  currency: string;
  timezone: string;
  trackingStartDate: ISODate;
  ownerProfileId: UUID;
  revision: string;
}

export interface Membership {
  householdId: UUID;
  profileId: UUID;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'removed';
  profile: Profile;
}

export interface Category {
  id: UUID;
  householdId: UUID;
  label: string;
  icon: string;
  color: string;
  sortOrder: number;
  favorite: boolean;
  archived: boolean;
  protected: boolean;
  version: number;
}

export type PaymentMethodType = 'cash' | 'debit_card' | 'credit_card' | 'bank_transfer' | 'e_wallet' | 'other';
export interface PaymentMethod {
  id: UUID;
  householdId: UUID;
  label: string;
  type: PaymentMethodType;
  ownerProfileId?: UUID | null;
  lastFour?: string | null;
  icon: string;
  color: string;
  sortOrder: number;
  archived: boolean;
  version: number;
}

export interface Allocation {
  id: UUID;
  householdId: UUID;
  transactionId: UUID;
  categoryId: UUID;
  amountMinor: MoneyString;
}

export interface Transaction {
  id: UUID;
  householdId: UUID;
  type: EntryType;
  amountMinor: MoneyString;
  currency: string;
  eventDate: ISODate;
  merchant: string;
  note: string;
  paymentMethodId?: UUID | null;
  transferDestinationMethodId?: UUID | null;
  payerKind: 'member' | 'household';
  paidByProfileId?: UUID | null;
  createdByProfileId: UUID;
  updatedByProfileId: UUID;
  originalExpenseId?: UUID | null;
  recurringOccurrenceId?: UUID | null;
  source: 'manual' | 'receipt_scan' | 'payment_slip_scan';
  sourceDocumentId?: UUID | null;
  scanDocument?: ScanDocumentInput;
  version: number;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  allocations: Allocation[];
}

export interface MonthlyBudget {
  id: UUID;
  householdId: UUID;
  periodStart: ISODate;
  totalMinor?: MoneyString | null;
  version: number;
  allocations: Array<{ id: UUID; categoryId: UUID; amountMinor: MoneyString }>;
}

export interface RecurringOccurrence {
  id: UUID;
  householdId: UUID;
  templateId: UUID;
  dueDate: ISODate;
  originalNominalDate: ISODate;
  expectedAmountMinor: MoneyString;
  status: 'planned' | 'confirmed' | 'skipped' | 'superseded';
  actualTransactionId?: UUID | null;
  version: number;
}

export interface RecurringTemplate {
  id: UUID; householdId: UUID; name: string; expectedAmountMinor: MoneyString; paymentMethodId?: UUID|null; payerKind:'member'|'household'; paidByProfileId?:UUID|null; frequency:'weekly'|'monthly'|'yearly'; intervalCount:number; anchorDate:ISODate; anchorDay:number; reminderOffsetDays:number; scheduleGeneration:number; active:boolean; version:number; allocations:Array<{id:UUID;categoryId:UUID;amountMinor:MoneyString}>;
}

export interface NotificationEvent {
  id: UUID;
  householdId: UUID;
  type: string;
  title: string;
  body: string;
  createdAt: string;
  readAt?: string | null;
}

export interface HouseholdSnapshot {
  revision: string;
  household: Household;
  memberships: Membership[];
  categories: Category[];
  paymentMethods: PaymentMethod[];
  transactions: Transaction[];
  budgets: MonthlyBudget[];
  recurringTemplates: RecurringTemplate[];
  recurringOccurrences: RecurringOccurrence[];
  notifications: NotificationEvent[];
}

export interface Operation<T = unknown> {
  opId: UUID;
  householdId: UUID;
  entityId: UUID;
  type: string;
  schemaVersion: 1;
  payload: T;
  expectedVersion: number | null;
  predecessorOpId?: UUID | null;
  createdAt: string;
  attempts: number;
  state: SyncState;
  error?: string;
  acknowledgedRevision?: string;
  acknowledgedEntityVersion?: number;
}

export interface ExpenseInput {
  id: UUID;
  householdId: UUID;
  type: EntryType;
  amountMinor: MoneyString;
  eventDate: ISODate;
  merchant: string;
  note: string;
  paymentMethodId?: UUID | null;
  transferDestinationMethodId?: UUID | null;
  payerKind: 'member' | 'household';
  paidByProfileId?: UUID | null;
  originalExpenseId?: UUID | null;
  recurringOccurrenceId?: UUID | null;
  source?: 'manual' | 'receipt_scan' | 'payment_slip_scan';
  scanDocument?: ScanDocumentInput;
  allocations: Array<{ id: UUID; categoryId: UUID; amountMinor: MoneyString }>;
}

export const syncCollections = ['memberships', 'categories', 'paymentMethods', 'transactions', 'budgets', 'recurringTemplates', 'recurringOccurrences'] as const;
export type SyncCollection = (typeof syncCollections)[number];
export interface HouseholdDelta extends HouseholdSnapshot {
  kind: 'delta';
  fromRevision: string;
  removed: Record<SyncCollection, string[]>;
}
export type HouseholdChanges = HouseholdDelta | { kind: 'snapshot'; snapshot: HouseholdSnapshot };
